import './Overview.scss';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  BrandHealthTimeSeries,
  IncrementalRoasTakeaways,
  ModelOverview
} from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  capitalizeWords,
  CHANNEL_NAME_MAP,
  currencyFormatter,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer/ChartContainer';
import { AreaChart } from '@Components/Charts/AreaChart/AreaChart';
import { headerFormatter } from '@Components/Charts/ChartUtils';
import { LineChart } from '@Components/Charts/LineChart/LineChart';
import { Placement, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { DynamicModelOverview } from '@Components/DynamicModelOverview/DynamicModelOverview';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { Channel1, Channel2, Channel3, Channel4 } from '@src/utils/colors';
import { dateToMddyy } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import {
  DATE_GROUPING_OPTIONS_BRAND_EQUITY,
  formatModelOverviewOutcomeVariable
} from '../BrandEquityUtils';
import { buildModelOverviewSections } from '../buildModelOverviewSections';
import { isQueryVolumeMetric } from '../metricNormalization';
import { useGroupedByDate } from '../useGroupedByDate';
import {
  keyGeneratorForChannelTacticBrandGrouping,
  standardizeMetricName
} from './BrandImpactUtils';

interface WeeklyBrandHealthAreaChart {
  date: string;
  channel: string;
  platform?: string;
  tactic?: string;
  ['brand/nonbrand']?: string;
  spend: number;
}

interface OverviewProps {
  groupByMetric: string;
  groupByLevel: string;
  weeklyBrandHealthTimeSeries: BrandHealthTimeSeries[];
  weeklyBrandHealthAreaChartSpend: Record<string, WeeklyBrandHealthAreaChart[]>;
  modelOverviewAll: Record<string, object>;
  incrementalRoasTakeaways: IncrementalRoasTakeaways;
  isQv: boolean;
  queryVolumeLongLabel: string;
  queryVolumeShortLabel: string;
}

const colors = [Channel1, Channel3, Channel2, Channel4];

export const Overview = ({
  groupByMetric,
  groupByLevel,
  weeklyBrandHealthTimeSeries,
  weeklyBrandHealthAreaChartSpend,
  modelOverviewAll,
  incrementalRoasTakeaways,
  isQv,
  queryVolumeLongLabel,
  queryVolumeShortLabel
}: OverviewProps): ReactElement => {
  const groupByMetricName = isQueryVolumeMetric(groupByMetric)
    ? queryVolumeShortLabel
    : groupByMetric;
  const [weeklyBrandHealthChartDateGrouping, setWeeklyBrandHealthChartDateGrouping] =
    useState<DateGroupingKey>('Week');
  const [weeklySpendChartDateGrouping, setWeeklySpendChartDateGrouping] =
    useState<DateGroupingKey>('Week');
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const weeklyBrandHealthTimeSeriesByDate = useGroupedByDate(
    weeklyBrandHealthTimeSeries,
    weeklyBrandHealthChartDateGrouping
  );

  const keysWithoutDate = useMemo(
    () =>
      Object.keys(weeklyBrandHealthTimeSeries[0])
        .filter(key => key !== 'date')
        .sort(),
    [weeklyBrandHealthTimeSeries]
  );

  const modelOverview = modelOverviewAll[groupByMetric] as ModelOverview;
  const [areaChartKeys, setAreaChartKeys] = useState<{ name: string; dataKey: string }[]>([]);

  const weeklyBrandHealthSpendAreaChart = useMemo(() => {
    if (Object.values(weeklyBrandHealthAreaChartSpend).length !== 0) {
      let finalAreaChart: any[];
      switch (groupByLevel) {
        case 'channel':
          finalAreaChart = weeklyBrandHealthAreaChartSpend[groupByMetric].reduce(
            (total, current) => {
              const existingRow = total.find(r => r.date === current.date);
              if (existingRow) {
                const levelName = current[groupByLevel];
                if (levelName in existingRow) {
                  existingRow[levelName] += current.spend;
                } else {
                  existingRow[levelName] = current.spend;
                }
              } else {
                const levelName = current[groupByLevel];
                total.push({ date: current.date, [levelName]: current.spend });
              }
              return total;
            },
            [] as any[]
          );
          break;
        case 'channel, platform':
          finalAreaChart = weeklyBrandHealthAreaChartSpend[groupByMetric].reduce(
            (total, current) => {
              const existingRow = total.find(r => r.date === current.date);
              const key = keyGeneratorForChannelTacticBrandGrouping(
                current.channel,
                current.platform
              );
              if (existingRow) {
                if (key in existingRow) {
                  existingRow[key] += current.spend;
                } else {
                  existingRow[key] = current.spend;
                }
              } else {
                total.push({
                  date: current.date,
                  [key]: current.spend
                });
              }
              return total;
            },
            [] as any[]
          );
          break;
        default:
          finalAreaChart = weeklyBrandHealthAreaChartSpend[groupByMetric].reduce(
            (total, current) => {
              const existingRow = total.find(r => r.date === current.date);

              const key = keyGeneratorForChannelTacticBrandGrouping(
                current.channel,
                current.platform,
                current.tactic || '',
                current['brand/nonbrand'] || ''
              );

              if (existingRow) {
                if (key in existingRow) {
                  existingRow[key] += current.spend;
                } else {
                  existingRow[key] = current.spend;
                }
              } else {
                total.push({
                  date: current.date,
                  [key]: current.spend
                });
              }

              return total;
            },
            [] as any[]
          );
      }
      return finalAreaChart;
    }
    return [];
  }, [groupByLevel, groupByMetric, weeklyBrandHealthAreaChartSpend]);

  const finalWeeklyBrandHealthAreaChartData = useGroupedByDate(
    weeklyBrandHealthSpendAreaChart,
    weeklySpendChartDateGrouping
  );

  useEffect(() => {
    const areaChartEntry = finalWeeklyBrandHealthAreaChartData[0];
    const filteredAreaChartEntry = Object.fromEntries(
      Object.entries(areaChartEntry).filter(([key]) => key !== 'date' && key !== 'covariates')
    );
    const finalAreaChartKeys = Object.entries(filteredAreaChartEntry).map(([key]) => {
      const name = key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return {
        name: CHANNEL_NAME_MAP[name.toLowerCase()] ?? name,
        dataKey: key
      };
    });

    setAreaChartKeys(finalAreaChartKeys);
  }, [finalWeeklyBrandHealthAreaChartData]);

  const excelDownloadWeeklyBrandHealth = useCallback(() => {
    exportToExcel(weeklyBrandHealthTimeSeriesByDate, 'weekly_brand_health');
  }, [weeklyBrandHealthTimeSeriesByDate]);

  const excelDownloadWeeklySpend = useCallback(() => {
    exportToExcel(finalWeeklyBrandHealthAreaChartData, 'weekly_spend');
  }, [finalWeeklyBrandHealthAreaChartData]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel([modelOverview], 'model_overview');
  }, [modelOverview]);

  const pngDownloadWeeklyBrandHealth = useCallback(async () => {
    await downloadPNG('.brand-equityleft .chart-container', 'weekly_brand_health');
  }, []);

  const pngDownloadWeeklySpend = useCallback(async () => {
    await downloadPNG('.brand-equityleft > .chart-container:nth-child(2)', 'weekly_spend');
  }, []);

  const pngDownloadModelOverview = useCallback(async () => {
    await downloadPNG('.brand-equity-overview .brand-equity-right', 'weekly_spend');
  }, []);

  return (
    <WidgetContainer
      collapsible
      header='Overview'
      subHeader={
        <>
          Assessing the impact of paid media on discrete brand health outcomes
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            The goal is to understand and quantify the independent effect of paid media on a set of
            brand health outcomes. We employ a multivariate time series model that regresses each
            brand outcome on the set of paid media activities and a series of control variables. In
            the estimation, the model accounts for non-linearities in effects and the often
            significant timing differences between inputs and outputs.
          </InfoTooltip>
        </>
      }
    >
      {Object.keys(modelOverview).length !== 0 &&
        weeklyBrandHealthTimeSeriesByDate.length !== 0 &&
        finalWeeklyBrandHealthAreaChartData.length !== 0 &&
        areaChartKeys.length !== 0 && (
          <div className='brand-equity-overview'>
            <div className='brand-equityleft'>
              <ChartContainer
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[excelDownloadWeeklyBrandHealth, pngDownloadWeeklyBrandHealth]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyBrandHealthChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyBrandHealthChartDateGrouping(option)}
                    />
                    <div>Brand Health</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyBrandHealthTimeSeries[0].date
                )} – ${dateToMddyy(
                  weeklyBrandHealthTimeSeries[weeklyBrandHealthTimeSeries.length - 1].date
                )}`}
                beforeTooltipText={isQv ? 'Search Intent' : 'Positive Response'}
                tooltipText={
                  isQv
                    ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                    : 'Positive response indicates the percentage of survey respondents expressing favorable perceptions or experiences related to each brand health metric.'
                }
              >
                <LineChart
                  customTooltipHeader={val =>
                    headerFormatter(weeklyBrandHealthChartDateGrouping, val, fiscalCal)
                  }
                  data={weeklyBrandHealthTimeSeriesByDate}
                  lineDataKeys={keysWithoutDate.map(key => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        )
                          .charAt(0)
                          .toUpperCase() +
                        String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        ).slice(1),
                    dataKey: key,
                    toolTipText: isQv
                      ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                      : 'ToolTipText'
                  }))}
                  colorMappings={keysWithoutDate.map((key, index) => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        )
                          .charAt(0)
                          .toUpperCase() +
                        String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        ).slice(1),
                    color: colors[index]
                  }))}
                  tooltipLabelFormatter={val => {
                    if (isQv) return queryVolumeShortLabel;
                    if (val === 'Awareness') return 'Aided Awareness';
                    return capitalizeWords(val);
                  }}
                  tooltipFormatter={val =>
                    isQv ? val.toFixed(2) : `${numberFormatter.format(val, 0)}%`
                  }
                  tooltipShape='line'
                  usePercentageYAxis={!isQv}
                  useLeftYAxis={isQv}
                  xAxisDataKey='date'
                  xAxisTickFormatter={val => dateToMddyy(val)}
                />
              </ChartContainer>
              <ChartContainer
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[excelDownloadWeeklySpend, pngDownloadWeeklySpend]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklySpendChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklySpendChartDateGrouping(option)}
                    />
                    <div>Spend</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyBrandHealthSpendAreaChart[0].date
                )} – ${dateToMddyy(
                  weeklyBrandHealthSpendAreaChart[weeklyBrandHealthSpendAreaChart.length - 1].date
                )}`}
              >
                <AreaChart
                  data={finalWeeklyBrandHealthAreaChartData}
                  xAxisDataKey='date'
                  xAxisTickFormatter={val => dateToMddyy(val)}
                  dateGrouping={weeklySpendChartDateGrouping}
                  yAxisStaticWidth={75}
                  yAxisTickFormatter={val => currencyFormatter.format(val, 0)}
                  tooltipFormatter={val => {
                    if (!val) {
                      return val;
                    }
                    return val < 1000
                      ? currencyFormatter.format(val, 0)
                      : currencyFormatter.format(val, 1);
                  }}
                  areas={areaChartKeys}
                />
              </ChartContainer>
            </div>
            <div className='brand-equity-right'>
              <DynamicModelOverview
                sections={buildModelOverviewSections(
                  modelOverview,
                  incrementalRoasTakeaways,
                  formatModelOverviewOutcomeVariable(groupByMetricName),
                  modelOverview.refresh_date || modelOverview.refresh_timestamp
                )}
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[excelDownloadModelOverview, pngDownloadModelOverview]}
                    menuOptions={['XLSX', 'PNG']}
                  />
                }
              />
            </div>
          </div>
        )}
    </WidgetContainer>
  );
};
